import * as React from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, TransformControls, Grid, GizmoHelper, GizmoViewport } from "@react-three/drei";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Move, RotateCcw, Eye, EyeOff, RefreshCw, Upload, Undo2, Redo2, Send, Info, Magnet } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent } from "../ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/Select";
import { Input } from "../ui/Input";
import { useToast } from "../ui/Toast";
import { getPlaneSingle, convertPlaneCoefs, editPlane } from "../../services/api";
import { useSpaceStore } from "../../stores/spaceStore";

interface PlaneData {
  id: string;
  name: string;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  visible: boolean;
  color: string;
  surfaceType: string;
  surfaceIndex: number;
  // Original plane coefficients (for direct editing and saving)
  coefficients: {
    a: number;
    b: number;
    c: number;
    d: number;
    vvx: number;
    vvy: number;
    vvz: number;
  };
}

interface PlaneEditorState {
  planes: PlaneData[];
  selectedPlaneId: string | null;
  mode: "translate" | "rotate";
  model: THREE.Group | null;
}

// Serialized version for history (plain objects instead of THREE.js classes)
interface SerializedPlaneData {
  id: string;
  name: string;
  position: { x: number; y: number; z: number };
  quaternion: { x: number; y: number; z: number; w: number };
  visible: boolean;
  color: string;
  surfaceType: string;
  surfaceIndex: number;
  coefficients: {
    a: number;
    b: number;
    c: number;
    d: number;
    vvx: number;
    vvy: number;
    vvz: number;
  };
}

interface SerializedState {
  planes: SerializedPlaneData[];
  selectedPlaneId: string | null;
  mode: "translate" | "rotate";
  model: null;
}

// Plane mesh component - no click handler here, handled by Scene raycasting
function PlaneMesh({
  plane,
  isSelected,
  meshRef,
}: {
  plane: PlaneData;
  isSelected: boolean;
  meshRef?: React.RefObject<THREE.Mesh>;
}) {
  const internalRef = React.useRef<THREE.Mesh>(null);
  const ref = meshRef || internalRef;

  React.useEffect(() => {
    if (ref.current) {
      ref.current.position.copy(plane.position);
      ref.current.quaternion.copy(plane.quaternion);
      // Store plane id on mesh for raycasting identification
      (ref.current as any).planeId = plane.id;
    }
  }, [plane.position, plane.quaternion, plane.id]);

  if (!plane.visible) return null;

  return (
    <mesh
      ref={ref}
      position={plane.position}
      quaternion={plane.quaternion}
    >
      <planeGeometry args={[3, 3]} />
      <meshPhongMaterial
        color={plane.color}
        transparent
        opacity={isSelected ? 0.7 : 0.5}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

// GLB Model component
function Model({ url }: { url: string | null }) {
  const [model, setModel] = React.useState<THREE.Group | null>(null);
  const { scene } = useThree();

  React.useEffect(() => {
    if (!url) {
      setModel(null);
      return;
    }

    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        const loadedModel = gltf.scene;

        // Apply coordinate system transformation (Y-up to Z-up)
        loadedModel.rotation.x = -Math.PI / 2;
        loadedModel.rotation.z = Math.PI;
        loadedModel.rotation.y = Math.PI;

        // Center and scale
        const box = new THREE.Box3().setFromObject(loadedModel);
        const center = box.getCenter(new THREE.Vector3());
        loadedModel.position.sub(center);

        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 10) {
          loadedModel.scale.setScalar(10 / maxDim);
        }

        setModel(loadedModel);
      },
      undefined,
      (error) => {
        console.error("Error loading GLB:", error);
      }
    );

    return () => {
      if (model) {
        scene.remove(model);
      }
    };
  }, [url]);

  if (!model) return null;

  return <primitive object={model} />;
}

// Transform controls wrapper with fine control support
function PlaneTransformControls({
  plane,
  mode,
  rotationSnap,
  translationSnap,
  onTransformEnd,
}: {
  plane: PlaneData;
  mode: "translate" | "rotate";
  rotationSnap: number | null;
  translationSnap: number | null;
  onTransformEnd: (position: THREE.Vector3, quaternion: THREE.Quaternion) => void;
}) {
  const meshRef = React.useRef<THREE.Mesh>(null);
  const transformRef = React.useRef<any>(null);
  const isShiftHeldRef = React.useRef(false);

  // Store previous values for delta calculation
  const prevPositionRef = React.useRef(plane.position.clone());
  const prevQuaternionRef = React.useRef(plane.quaternion.clone());
  const basePositionRef = React.useRef(plane.position.clone());
  const baseQuaternionRef = React.useRef(plane.quaternion.clone());

  // Track Shift key for fine control
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") isShiftHeldRef.current = true;
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") isShiftHeldRef.current = false;
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  React.useEffect(() => {
    if (meshRef.current) {
      meshRef.current.position.copy(plane.position);
      meshRef.current.quaternion.copy(plane.quaternion);
      prevPositionRef.current.copy(plane.position);
      prevQuaternionRef.current.copy(plane.quaternion);
      basePositionRef.current.copy(plane.position);
      baseQuaternionRef.current.copy(plane.quaternion);
    }
  }, [plane.position, plane.quaternion]);

  // Handle drag start - store base position
  const handleDragStart = () => {
    if (meshRef.current) {
      basePositionRef.current.copy(meshRef.current.position);
      baseQuaternionRef.current.copy(meshRef.current.quaternion);
      prevPositionRef.current.copy(meshRef.current.position);
      prevQuaternionRef.current.copy(meshRef.current.quaternion);
    }
  };

  // Handle transform change - apply fine control scaling
  const handleChange = () => {
    if (!meshRef.current || !isShiftHeldRef.current) {
      // Update prev refs even when not scaling
      if (meshRef.current) {
        prevPositionRef.current.copy(meshRef.current.position);
        prevQuaternionRef.current.copy(meshRef.current.quaternion);
      }
      return;
    }

    const fineScale = 0.1; // 10x slower when Shift held

    if (mode === "translate") {
      // Calculate delta from previous position
      const delta = new THREE.Vector3().subVectors(meshRef.current.position, prevPositionRef.current);
      // Scale the delta
      delta.multiplyScalar(fineScale);
      // Apply scaled delta to previous position
      meshRef.current.position.copy(prevPositionRef.current).add(delta);
    } else {
      // For rotation, interpolate between previous and current quaternion
      const currentQuat = meshRef.current.quaternion.clone();
      meshRef.current.quaternion.copy(prevQuaternionRef.current).slerp(currentQuat, fineScale);
    }

    // Update prev refs
    prevPositionRef.current.copy(meshRef.current.position);
    prevQuaternionRef.current.copy(meshRef.current.quaternion);
  };

  const handleTransformEnd = () => {
    if (meshRef.current) {
      onTransformEnd(
        meshRef.current.position.clone(),
        meshRef.current.quaternion.clone()
      );
    }
  };

  return (
    <>
      <mesh ref={meshRef} position={plane.position} quaternion={plane.quaternion}>
        <planeGeometry args={[3, 3]} />
        <meshPhongMaterial
          color={plane.color}
          transparent
          opacity={0.7}
          side={THREE.DoubleSide}
        />
      </mesh>
      <TransformControls
        ref={transformRef}
        object={meshRef.current!}
        mode={mode}
        space="world"
        rotationSnap={rotationSnap}
        translationSnap={translationSnap}
        onMouseDown={handleDragStart}
        onChange={handleChange}
        onMouseUp={handleTransformEnd}
      />
    </>
  );
}

// Click handler component for proper raycasting (selects nearest plane)
function PlaneClickHandler({
  planes,
  onPlaneSelect,
}: {
  planes: PlaneData[];
  onPlaneSelect: (id: string) => void;
}) {
  const { camera, scene, gl } = useThree();
  const raycaster = React.useMemo(() => new THREE.Raycaster(), []);

  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Get normalized device coordinates
      const rect = gl.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );

      raycaster.setFromCamera(mouse, camera);

      // Get all meshes with planeId
      const planeMeshes: THREE.Object3D[] = [];
      scene.traverse((obj) => {
        if ((obj as any).planeId && obj instanceof THREE.Mesh) {
          // Only include visible planes
          const plane = planes.find((p) => p.id === (obj as any).planeId);
          if (plane?.visible) {
            planeMeshes.push(obj);
          }
        }
      });

      // Raycast against plane meshes
      const intersects = raycaster.intersectObjects(planeMeshes, false);

      if (intersects.length > 0) {
        // Select the nearest (first) intersection
        const nearestPlaneId = (intersects[0].object as any).planeId;
        if (nearestPlaneId) {
          onPlaneSelect(nearestPlaneId);
        }
      }
    };

    gl.domElement.addEventListener("click", handleClick);
    return () => {
      gl.domElement.removeEventListener("click", handleClick);
    };
  }, [camera, scene, gl, raycaster, planes, onPlaneSelect]);

  return null;
}

// Scene component
function Scene({
  state,
  modelUrl,
  rotationSnap,
  translationSnap,
  onPlaneSelect,
  onTransformEnd,
}: {
  state: PlaneEditorState;
  modelUrl: string | null;
  rotationSnap: number | null;
  translationSnap: number | null;
  onPlaneSelect: (id: string) => void;
  onTransformEnd: (id: string, position: THREE.Vector3, quaternion: THREE.Quaternion) => void;
}) {
  const selectedPlane = state.planes.find((p) => p.id === state.selectedPlaneId);

  return (
    <>
      {/* Click handler for proper plane selection */}
      <PlaneClickHandler planes={state.planes} onPlaneSelect={onPlaneSelect} />

      {/* Lighting */}
      <ambientLight intensity={1.5} />
      <directionalLight position={[5, 10, 5]} intensity={1.2} castShadow />
      <directionalLight position={[-5, 10, -5]} intensity={1.0} />
      <directionalLight position={[0, -10, 0]} intensity={0.8} />
      <pointLight position={[10, 5, 0]} intensity={0.5} />
      <pointLight position={[-10, 5, 0]} intensity={0.5} />

      {/* Grid - rotated for Z-up */}
      <Grid
        args={[10, 10]}
        rotation={[Math.PI / 2, 0, 0]}
        cellSize={1}
        cellThickness={0.5}
        cellColor="#888888"
        sectionSize={5}
        sectionThickness={1}
        sectionColor="#444444"
        fadeDistance={30}
        fadeStrength={1}
      />

      {/* Axes helper */}
      <axesHelper args={[2]} />

      {/* GLB Model */}
      <Model url={modelUrl} />

      {/* Non-selected planes */}
      {state.planes
        .filter((p) => p.id !== state.selectedPlaneId)
        .map((plane) => (
          <PlaneMesh
            key={plane.id}
            plane={plane}
            isSelected={false}
          />
        ))}

      {/* Selected plane with transform controls */}
      {selectedPlane && selectedPlane.visible && (
        <PlaneTransformControls
          plane={selectedPlane}
          mode={state.mode}
          rotationSnap={rotationSnap}
          translationSnap={translationSnap}
          onTransformEnd={(pos, quat) => onTransformEnd(selectedPlane.id, pos, quat)}
        />
      )}

      {/* Camera controls */}
      <OrbitControls makeDefault enableDamping dampingFactor={0.05} />

      {/* Gizmo */}
      <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
        <GizmoViewport labelColor="white" axisHeadScale={1} />
      </GizmoHelper>
    </>
  );
}

// Generate colors for planes
function getPlaneColor(index: number): string {
  const hue = (index * 60) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

// Convert visual position/quaternion to Blender coordinates string for API
// This reverses the visual coordinate transforms we applied when loading
function visualToBlenderString(position: THREE.Vector3, quaternion: THREE.Quaternion): string {
  // Reverse the visual transforms:
  // When loading: fixedPosX = -locX, quatY = -quatY, quatZ = -quatZ
  // So to reverse: locX = -position.x, quatY = -quaternion.y, quatZ = -quaternion.z
  const locX = -position.x;
  const locY = position.y;
  const locZ = position.z;
  const quatW = quaternion.w;
  const quatX = quaternion.x;
  const quatY = -quaternion.y;
  const quatZ = -quaternion.z;

  // Format: locX,locY,locZ,quatW,quatX,quatY,quatZ
  return `${locX},${locY},${locZ},${quatW},${quatX},${quatY},${quatZ}`;
}

export function PlaneEditor3D() {
  const [state, setState] = React.useState<PlaneEditorState>({
    planes: [],
    selectedPlaneId: null,
    mode: "translate",
    model: null,
  });
  const [modelUrl, setModelUrl] = React.useState<string | null>(null);
  const [isLoadingPlanes, setIsLoadingPlanes] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [history, setHistory] = React.useState<SerializedState[]>([]);
  const [historyIndex, setHistoryIndex] = React.useState(-1);
  const [useSnap, setUseSnap] = React.useState(true);
  const [isModified, setIsModified] = React.useState(false);

  // Snap values: 5 degrees for rotation, 0.1 units for translation
  const rotationSnap = useSnap ? (5 * Math.PI) / 180 : null;
  const translationSnap = useSnap ? 0.1 : null;

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const { surfaces, spaceId } = useSpaceStore();

  const selectedPlane = state.planes.find((p) => p.id === state.selectedPlaneId);

  // Load planes from surfaces - uses convertPlaneCoefs for visualization position
  const handleLoadPlanes = async () => {
    if (surfaces.length === 0) {
      addToast("error", "No surfaces available");
      return;
    }

    setIsLoadingPlanes(true);
    try {
      const loadedPlanes: PlaneData[] = [];
      let colorIndex = 0;

      for (const surface of surfaces) {
        const surfaceType = surface.label.includes("wall") ? "walls" : surface.label.toLowerCase();
        const surfaceIndex = surface.index ?? 0;

        try {
          // Get plane coefficients
          const planeResponse = await getPlaneSingle(surfaceType, surfaceIndex, spaceId || undefined);
          if (planeResponse.status !== "SUCCESS" || !planeResponse.output) {
            console.warn(`No plane data for ${surface.label}`);
            continue;
          }

          const coeffs = planeResponse.output.split(",").map(Number);
          const [a, b, c, d, vvx, vvy, vvz] = coeffs;

          // Convert to Blender coordinates for visualization
          const convertResponse = await convertPlaneCoefs(
            planeResponse.output,
            surfaceType,
            surfaceIndex,
            false
          );

          if (convertResponse.status !== "SUCCESS" || !convertResponse.output) {
            console.warn(`Failed to convert plane for ${surface.label}`);
            continue;
          }

          const blenderCoords = convertResponse.output.split(",").map(Number);
          const [locX, locY, locZ, quatW, quatX, quatY, quatZ] = blenderCoords;

          // Fix horizontal flip: negate X position
          // For quaternion mirror across YZ plane: negate Y and Z components
          const fixedPosX = -locX;

          loadedPlanes.push({
            id: `${surfaceType}_${surfaceIndex}`,
            name: surface.label,
            position: new THREE.Vector3(fixedPosX, locY, locZ),
            quaternion: new THREE.Quaternion(quatX, -quatY, -quatZ, quatW),
            visible: true,
            color: getPlaneColor(colorIndex++),
            surfaceType,
            surfaceIndex,
            coefficients: { a, b, c, d, vvx, vvy, vvz },
          });
        } catch (err) {
          console.warn(`Error loading plane for ${surface.label}:`, err);
        }
      }

      const newState = {
        ...state,
        planes: loadedPlanes,
        selectedPlaneId: loadedPlanes[0]?.id || null,
      };

      setState(newState);

      // Reset history with initial state
      const serialized: SerializedState = {
        planes: serializePlanes(loadedPlanes),
        selectedPlaneId: loadedPlanes[0]?.id || null,
        mode: state.mode,
        model: null,
      };
      setHistory([serialized]);
      setHistoryIndex(0);
      setIsModified(false);

      addToast("success", `Loaded ${loadedPlanes.length} planes`);
    } catch (error) {
      addToast("error", "Failed to load planes");
    } finally {
      setIsLoadingPlanes(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setModelUrl(url);
      addToast("success", `Loaded: ${file.name}`);
    }
  };

  // Serialize plane data for history (properly extracts THREE.js object values)
  const serializePlanes = (planes: PlaneData[]): SerializedPlaneData[] => {
    return planes.map((p) => ({
      id: p.id,
      name: p.name,
      position: { x: p.position.x, y: p.position.y, z: p.position.z },
      quaternion: { x: p.quaternion.x, y: p.quaternion.y, z: p.quaternion.z, w: p.quaternion.w },
      visible: p.visible,
      color: p.color,
      surfaceType: p.surfaceType,
      surfaceIndex: p.surfaceIndex,
      coefficients: { ...p.coefficients },
    }));
  };

  // Deserialize plane data from history
  const deserializePlanes = (planes: SerializedPlaneData[]): PlaneData[] => {
    return planes.map((p) => ({
      id: p.id,
      name: p.name,
      position: new THREE.Vector3(p.position.x, p.position.y, p.position.z),
      quaternion: new THREE.Quaternion(p.quaternion.x, p.quaternion.y, p.quaternion.z, p.quaternion.w),
      visible: p.visible,
      color: p.color,
      surfaceType: p.surfaceType,
      surfaceIndex: p.surfaceIndex,
      coefficients: { ...p.coefficients },
    }));
  };

  // Save current state to history
  const saveToHistory = React.useCallback((currentState: PlaneEditorState) => {
    const serialized: SerializedState = {
      planes: serializePlanes(currentState.planes),
      selectedPlaneId: currentState.selectedPlaneId,
      mode: currentState.mode,
      model: null,
    };

    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(serialized);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  // Undo
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const prevState = history[newIndex];
      setHistoryIndex(newIndex);
      setState((curr) => ({
        planes: deserializePlanes(prevState.planes),
        selectedPlaneId: prevState.selectedPlaneId,
        mode: prevState.mode,
        model: curr.model,
      }));
      addToast("info", `Undo (${newIndex + 1}/${history.length})`);
    }
  };

  // Redo
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setState((curr) => ({
        planes: deserializePlanes(nextState.planes),
        selectedPlaneId: nextState.selectedPlaneId,
        mode: nextState.mode,
        model: curr.model,
      }));
      addToast("info", `Redo (${newIndex + 1}/${history.length})`);
    }
  };

  // Handle plane selection
  const handlePlaneSelect = (id: string) => {
    setState((prev) => ({ ...prev, selectedPlaneId: id }));
  };

  // Handle transform end - only updates visual position/quaternion
  // Coefficients will be computed on save via server-side conversion
  const handleTransformEnd = (id: string, position: THREE.Vector3, quaternion: THREE.Quaternion) => {
    setState((prev) => {
      const newState = {
        ...prev,
        planes: prev.planes.map((p) =>
          p.id === id
            ? {
                ...p,
                position: position.clone(),
                quaternion: quaternion.clone(),
              }
            : p
        ),
      };
      saveToHistory(newState);
      return newState;
    });
    setIsModified(true);
  };

  // Toggle plane visibility
  const handleToggleVisibility = () => {
    if (!selectedPlane) return;
    setState((prev) => ({
      ...prev,
      planes: prev.planes.map((p) =>
        p.id === prev.selectedPlaneId ? { ...p, visible: !p.visible } : p
      ),
    }));
  };

  // Reset plane to original loaded values
  const handleResetPlane = () => {
    if (!selectedPlane || historyIndex < 0) return;
    const originalState = history[0];
    if (!originalState) return;

    const originalPlane = originalState.planes.find((p) => p.id === selectedPlane.id);
    if (!originalPlane) return;

    setState((prev) => {
      const newState = {
        ...prev,
        planes: prev.planes.map((p) =>
          p.id === prev.selectedPlaneId
            ? {
                ...p,
                position: new THREE.Vector3(originalPlane.position.x, originalPlane.position.y, originalPlane.position.z),
                quaternion: new THREE.Quaternion(originalPlane.quaternion.x, originalPlane.quaternion.y, originalPlane.quaternion.z, originalPlane.quaternion.w),
                coefficients: { ...originalPlane.coefficients },
              }
            : p
        ),
      };
      saveToHistory(newState);
      return newState;
    });
    addToast("info", "Plane reset to original");
  };

  // Update plane - converts visual position/quaternion to coefficients via server
  // This mirrors how gradio 3D viewer works: position/quaternion → convertPlaneCoefs(reverse=true) → editPlane
  const handleUpdatePlane = async () => {
    if (!selectedPlane) return;

    // Check if anything has changed
    if (!isModified) {
      addToast("info", "No changes to save");
      return;
    }

    setIsSaving(true);
    try {
      // Convert visual position/quaternion to Blender coordinate string
      const blenderString = visualToBlenderString(selectedPlane.position, selectedPlane.quaternion);

      console.log("Saving plane:", selectedPlane.name);
      console.log("Visual pos:", selectedPlane.position.toArray());
      console.log("Visual quat:", [selectedPlane.quaternion.x, selectedPlane.quaternion.y, selectedPlane.quaternion.z, selectedPlane.quaternion.w]);
      console.log("Blender string:", blenderString);

      // Call convertPlaneCoefs with reverse=true to get plane coefficients from Blender coordinates
      const convertResponse = await convertPlaneCoefs(
        blenderString,
        selectedPlane.surfaceType,
        selectedPlane.surfaceIndex,
        true // reverse=true to convert FROM Blender coords TO plane coefficients
      );

      if (convertResponse.status !== "SUCCESS" || !convertResponse.output) {
        throw new Error(convertResponse.message || "Failed to convert coordinates to coefficients");
      }

      console.log("Converted coefficients:", convertResponse.output);

      // Parse the coefficients from the server response
      const coeffs = convertResponse.output.split(",").map(Number);
      const [a, b, c, d, vvx, vvy, vvz] = coeffs;

      // Update plane in backend with the converted coefficients
      const editResponse = await editPlane(
        selectedPlane.surfaceType,
        selectedPlane.surfaceIndex,
        a, b, c, d, vvx, vvy, vvz,
        spaceId || undefined
      );

      if (editResponse.status !== "SUCCESS") {
        throw new Error(editResponse.message || "Update failed");
      }

      // Update local coefficients to match what was saved
      setState((prev) => ({
        ...prev,
        planes: prev.planes.map((p) =>
          p.id === selectedPlane.id
            ? { ...p, coefficients: { a, b, c, d, vvx, vvy, vvz } }
            : p
        ),
      }));

      setIsModified(false);
      addToast("success", `Updated ${selectedPlane.name}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update plane";
      addToast("error", message);
      console.error("Update plane error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Update coefficient input (for manual editing)
  const handleCoeffChange = (field: string, value: string) => {
    if (!selectedPlane) return;

    const numValue = parseFloat(value) || 0;
    setState((prev) => ({
      ...prev,
      planes: prev.planes.map((p) => {
        if (p.id !== prev.selectedPlaneId) return p;
        return {
          ...p,
          coefficients: {
            ...p.coefficients,
            [field]: numValue,
          },
        };
      }),
    }));
    setIsModified(true);
  };

  if (!spaceId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Load a space first to edit geometry
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
          <Button variant="default" size="sm" onClick={handleLoadPlanes} loading={isLoadingPlanes}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Load Planes
          </Button>
          <div className="border-l mx-2 h-6" />
          <Button variant="outline" size="sm" onClick={handleUndo} disabled={historyIndex <= 0}>
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
            <Redo2 className="h-4 w-4" />
          </Button>
          <Button
            variant={useSnap ? "default" : "outline"}
            size="sm"
            onClick={() => setUseSnap(!useSnap)}
            title={useSnap ? "Snap enabled (5° rotation, 0.1 position)" : "Free transform"}
          >
            <Magnet className="h-4 w-4 mr-1" />
            Snap
          </Button>
          <div className="border-l mx-2 h-6" />
          <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} title="Upload your own GLB mesh (optional)">
            <Upload className="h-4 w-4 mr-1" />
            Upload GLB
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".glb,.gltf"
            onChange={handleFileUpload}
            className="hidden"
          />
          {state.planes.length > 0 && (
            <span className="text-sm text-muted-foreground ml-auto flex items-center gap-2">
              {isModified && <span className="text-yellow-600 font-medium">* Modified</span>}
              {state.planes.length} plane{state.planes.length !== 1 ? 's' : ''} loaded
            </span>
          )}
        </div>

        {/* 3D Canvas */}
        <div className="h-[500px] bg-gradient-to-b from-gray-200 to-gray-100 rounded-lg overflow-hidden border">
          <Canvas
            camera={{ position: [3, -3, 3], up: [0, 0, 1], fov: 75 }}
            shadows
          >
            <Scene
              state={state}
              modelUrl={modelUrl}
              rotationSnap={rotationSnap}
              translationSnap={translationSnap}
              onPlaneSelect={handlePlaneSelect}
              onTransformEnd={handleTransformEnd}
            />
          </Canvas>
        </div>

        {/* Controls Panel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Plane Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Plane</label>
            <Select
              value={state.selectedPlaneId || ""}
              onValueChange={handlePlaneSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a plane..." />
              </SelectTrigger>
              <SelectContent>
                {state.planes.map((plane) => (
                  <SelectItem key={plane.id} value={plane.id}>
                    {plane.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant={state.mode === "translate" ? "default" : "outline"}
                size="sm"
                onClick={() => setState((prev) => ({ ...prev, mode: "translate" }))}
                className="flex-1"
              >
                <Move className="h-4 w-4 mr-1" />
                Move
              </Button>
              <Button
                variant={state.mode === "rotate" ? "default" : "outline"}
                size="sm"
                onClick={() => setState((prev) => ({ ...prev, mode: "rotate" }))}
                className="flex-1"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Rotate
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleToggleVisibility} className="flex-1">
                {selectedPlane?.visible ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                Toggle
              </Button>
              <Button variant="outline" size="sm" onClick={handleResetPlane} className="flex-1">
                <RefreshCw className="h-4 w-4 mr-1" />
                Reset
              </Button>
            </div>
          </div>

          {/* Position (read-only display) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Position (visual)</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">X</label>
                <Input
                  type="number"
                  step="0.1"
                  value={selectedPlane?.position.x.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Y</label>
                <Input
                  type="number"
                  step="0.1"
                  value={selectedPlane?.position.y.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Z</label>
                <Input
                  type="number"
                  step="0.1"
                  value={selectedPlane?.position.z.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
            </div>
          </div>

          {/* Quaternion (read-only display) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Rotation (visual)</label>
            <div className="grid grid-cols-4 gap-1">
              <div>
                <label className="text-xs text-muted-foreground">W</label>
                <Input
                  type="number"
                  value={selectedPlane?.quaternion.w.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">X</label>
                <Input
                  type="number"
                  value={selectedPlane?.quaternion.x.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Y</label>
                <Input
                  type="number"
                  value={selectedPlane?.quaternion.y.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Z</label>
                <Input
                  type="number"
                  value={selectedPlane?.quaternion.z.toFixed(3) || ""}
                  disabled
                  className="text-xs"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Plane Coefficients - Editable */}
        {selectedPlane?.coefficients && (
          <div className="p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Plane Coefficients (editable)</span>
            </div>
            <div className="grid grid-cols-7 gap-2 text-xs">
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">a</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.a.toFixed(4)}
                  onChange={(e) => handleCoeffChange("a", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">b</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.b.toFixed(4)}
                  onChange={(e) => handleCoeffChange("b", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">c</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.c.toFixed(4)}
                  onChange={(e) => handleCoeffChange("c", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">d</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.d.toFixed(4)}
                  onChange={(e) => handleCoeffChange("d", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">vvx</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.vvx.toFixed(4)}
                  onChange={(e) => handleCoeffChange("vvx", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">vvy</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.vvy.toFixed(4)}
                  onChange={(e) => handleCoeffChange("vvy", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
              <div className="text-center">
                <span className="text-muted-foreground block mb-1">vvz</span>
                <Input
                  type="number"
                  step="0.01"
                  value={selectedPlane.coefficients.vvz.toFixed(4)}
                  onChange={(e) => handleCoeffChange("vvz", e.target.value)}
                  className="font-mono text-xs h-8"
                />
              </div>
            </div>
          </div>
        )}

        {/* Update Button */}
        <Button
          onClick={handleUpdatePlane}
          loading={isSaving}
          disabled={!selectedPlane}
          className="w-full"
        >
          <Send className="h-4 w-4 mr-2" />
          Update Plane Geometry
        </Button>
    </div>
  );
}
